import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plane, Sparkles, ShieldCheck, FileText, Building2, Compass, BookOpen, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookDemoDialog } from "@/components/BookDemoDialog";
import { CountryDetailsModal } from "@/components/CountryDetailsModal";
import { COUNTRIES, FACILITIES } from "@/data/countries";
import { FoundingProfileCard } from "@/components/FoundingProfileCard";
import { api } from "@/lib/api";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 26 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const steps = [
  { Icon: Compass, title: "Profile evaluation", desc: "We audit your academics, budget & ambitions to shortlist ideal countries." },
  { Icon: Building2, title: "University shortlisting", desc: "3-tier list — ambitious, target, and safe — mapped to your intake." },
  { Icon: FileText, title: "SOP & docs", desc: "Story-first SOPs, LORs, CV, and financial docs — polished to visa standards." },
  { Icon: ShieldCheck, title: "Visa & interview", desc: "Filing, mock interviews, and pre-departure — until you land safely." },
];

export default function ImmigrationDashboard() {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [extra, setExtra] = useState({ facilities: [], countries: [] });

  useEffect(() => {
    api.get("/immigration/content").then((r) => setExtra(r.data)).catch(() => {});
  }, []);

  const allCountries = useMemo(() => {
    const facilities = [...FACILITIES, ...(extra.facilities || []).map((f) => f.name)];
    const custom = (extra.countries || []).map((c) => ({
      id: c.id,
      code: c.code || (c.name || "XX").slice(0, 2).toUpperCase(),
      flag: c.flag || "🌍",
      name: c.name,
      tag: c.tag || "New destination",
      description: c.description || "",
      universities: (c.universities || []).map((n) => ({
        name: n,
        logo: `https://placehold.co/80x80/D90429/ffffff/png?text=${encodeURIComponent(n.split(" ").map((w) => w[0]).join("").slice(0, 4))}&font=montserrat`,
      })),
      facilities,
    }));
    return [...COUNTRIES.map((c) => ({ ...c, facilities })), ...custom];
  }, [extra]);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />

      <section className="relative pt-16 pb-20 overflow-hidden bg-brand-navy text-white">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-brand-red/30 blur-3xl animate-blob" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-brand-gold/20 blur-3xl animate-blob" style={{ animationDelay: "5s" }} />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px)", backgroundSize: "36px 36px" }}
        />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[minmax(0,1fr)_auto] gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={stagger} className="max-w-3xl">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-gold text-brand-navy text-xs font-semibold uppercase tracking-widest">
              <Sparkles className="w-3.5 h-3.5" /> Immigration & Study Abroad
            </motion.div>
            <motion.h1 variants={fadeUp} className="mt-6 font-heading font-black text-5xl md:text-6xl leading-[1.05]">
              From SOP to <span className="text-brand-gold">boarding pass.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-5 text-lg text-white/70 max-w-2xl">
              27+ countries. 92% visa success. A single consultant who owns your file from Day 1 to landing. No sub-agents,
              no runaround, no jargon.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
              <BookDemoDialog
                defaultCourse="Immigration Consultation"
                leadType="immigration"
                trigger={
                  <Button data-testid="immig-hero-demo-btn" className="rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold px-7 h-12 brand-shadow btn-scale">
                    Book Free Consultation
                  </Button>
                }
              />
              <a href="#countries">
                <Button variant="outline" className="rounded-full border-2 border-white text-white bg-transparent hover:bg-white hover:text-brand-navy font-semibold px-7 h-12 btn-scale">
                  Browse Countries
                </Button>
              </a>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-10 grid grid-cols-3 gap-5 max-w-lg">
              {[
                { n: "92%", l: "Visa success" },
                { n: "27+", l: "Countries" },
                { n: "500+", l: "Placements last year" },
              ].map((s) => (
                <div key={s.l} className="rounded-2xl bg-white/10 border border-white/15 px-4 py-3 backdrop-blur-md">
                  <div className="font-heading font-black text-2xl text-brand-gold">{s.n}</div>
                  <div className="text-xs text-white/70 mt-1">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
            className="hidden lg:flex justify-end"
          >
            <FoundingProfileCard
              theme="dark"
              testId="immigration-founder-card"
              name="Ashish Garg"
              shortTitle="Founder, Language Nest"
              photo="https://customer-assets-wrfwihn1.emergentagent.net/job_vibrant-language-hub/artifacts/pzn03s4m_image.png"
              titles={["Founder, Language Nest"]}
              bio={"Language Nest represents a natural extension of that same instinct — for connecting people, institutions, and opportunities. Founded by Er. Ashish Garg, it applies his infrastructure-honed rigor to a different kind of building: international education pathways, institutional partnerships, and workforce mobility.\n\nWhat ties his work together — whether it's a highway project or a university partnership — is a consistent ability to bring the right people to the table: institutions, and global partners, across India and abroad."}
              workList={[
                "International education and student mobility",
                "Foreign university collaborations",
                "Institutional development and academic consulting",
                "Skill development and training programs",
                "Corporate training and leadership development",
                "International recruitment and workforce solutions",
                "Business expansion and strategic partnerships",
                "Infrastructure and project consulting",
                "Funding, CSR, and government liaison support",
              ]}
            />
          </motion.div>
        </div>
      </section>

      {/* Immigration founder card — mobile */}
      <section className="lg:hidden -mt-6 mb-8 px-6">
        <div className="max-w-md mx-auto">
          <FoundingProfileCard
            theme="light"
            testId="immigration-founder-card-mobile"
            name="Ashish Garg"
            shortTitle="Founder, Language Nest"
            photo="https://customer-assets-wrfwihn1.emergentagent.net/job_vibrant-language-hub/artifacts/pzn03s4m_image.png"
            titles={["Founder, Language Nest"]}
            bio={"Language Nest represents a natural extension of that same instinct — for connecting people, institutions, and opportunities. Founded by Er. Ashish Garg, it applies his infrastructure-honed rigor to a different kind of building: international education pathways, institutional partnerships, and workforce mobility.\n\nWhat ties his work together — whether it's a highway project or a university partnership — is a consistent ability to bring the right people to the table: institutions, and global partners, across India and abroad."}
            workList={[
              "International education and student mobility",
              "Foreign university collaborations",
              "Institutional development and academic consulting",
              "Skill development and training programs",
              "Corporate training and leadership development",
              "International recruitment and workforce solutions",
              "Business expansion and strategic partnerships",
              "Infrastructure and project consulting",
              "Funding, CSR, and government liaison support",
            ]}
          />
        </div>
      </section>

      {/* Country cards */}
      <section id="countries" className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">Destinations</div>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl text-brand-navy leading-tight">
              Pick your dream country. <span className="text-brand-red">We&#39;ll build the file.</span>
            </h2>
          </div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allCountries.map((c) => (
              <motion.div
                key={c.id || c.code}
                variants={fadeUp}
                data-testid={`country-card-${c.code.toLowerCase()}`}
                className="card-hover rounded-3xl bg-white border border-brand-navy/10 p-8 relative overflow-hidden flex flex-col"
              >
                <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full bg-brand-red/5" />
                <div className="absolute -bottom-16 -left-10 w-40 h-40 rounded-full bg-brand-red/[0.03]" />
                <div className="relative flex flex-col flex-1">
                  <div className="flex items-start justify-between">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: -6 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      className="text-6xl leading-none"
                      aria-label={c.name}
                    >
                      {c.flag}
                    </motion.div>
                    <Badge className="bg-brand-navy text-white hover:bg-brand-navy font-semibold rounded-full text-[11px]">
                      #{c.code}
                    </Badge>
                  </div>

                  <h3 className="mt-6 font-heading font-black text-2xl text-brand-navy">{c.name}</h3>
                  <p className="mt-2 text-sm text-brand-navy/60 leading-snug line-clamp-2">{c.tag}</p>

                  {c.universities.length > 0 && (
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-red/80">
                      <BookOpen className="w-3.5 h-3.5" />
                      Top {c.universities.length} universities included
                    </div>
                  )}

                  <Button
                    data-testid={`country-know-btn-${c.code.toLowerCase()}`}
                    onClick={() => setSelectedCountry(c)}
                    className="mt-auto pt-0 mt-8 w-full h-12 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold brand-shadow btn-scale group"
                  >
                    Know about Country
                    <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </motion.div>
            ))}

            {/* Other countries — generic enquiry tile */}
            <motion.div
              variants={fadeUp}
              data-testid="country-card-other"
              className="card-hover rounded-3xl bg-brand-navy text-white p-8 relative overflow-hidden flex flex-col"
            >
              <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full bg-brand-gold/15" />
              <div className="absolute -bottom-16 -left-10 w-40 h-40 rounded-full bg-brand-red/20" />
              <div className="relative flex flex-col flex-1">
                <div className="flex items-start justify-between">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: -6 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="text-6xl leading-none"
                    aria-label="Other countries"
                  >
                    🌍
                  </motion.div>
                  <Badge className="bg-brand-gold text-brand-navy hover:bg-brand-gold font-semibold rounded-full text-[11px]">
                    27+ destinations
                  </Badge>
                </div>

                <h3 className="mt-6 font-heading font-black text-2xl">Other Countries</h3>
                <p className="mt-2 text-sm text-white/70 leading-snug line-clamp-3">
                  Canada, Australia, UK, USA, Ireland, France, Netherlands and more — tell us where you dream of studying and we&#39;ll build the file.
                </p>

                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-gold">
                  <Compass className="w-3.5 h-3.5" />
                  Custom country consultation
                </div>

                <BookDemoDialog
                  defaultCourse="Immigration Consultation — Other Country"
                  leadType="immigration"
                  trigger={
                    <Button
                      data-testid="country-know-btn-other"
                      className="mt-auto pt-0 mt-8 w-full h-12 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold brand-shadow btn-scale group"
                    >
                      Enquire now
                      <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  }
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-brand-muted-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">Our 4-step process</div>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl text-brand-navy">Simple. Transparent. Yours to track.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={s.title} className="relative rounded-3xl bg-white p-7 border border-brand-navy/10 card-hover">
                <div className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-brand-navy text-brand-gold grid place-items-center font-heading font-black">
                  {i + 1}
                </div>
                <div className="w-12 h-12 rounded-2xl bg-brand-red/10 text-brand-red grid place-items-center mb-4">
                  <s.Icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading font-bold text-xl text-brand-navy">{s.title}</h3>
                <p className="text-sm text-brand-navy/60 mt-2">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="rounded-3xl bg-brand-red text-white p-10 md:p-14 relative overflow-hidden brand-shadow">
            <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-white/10" />
            <div className="absolute top-6 right-6 opacity-20"><Plane className="w-24 h-24" /></div>
            <div className="relative">
              <h3 className="font-heading font-black text-3xl md:text-4xl max-w-2xl leading-tight">
                Talk to a real study-abroad consultant — no bots, no callbacks.
              </h3>
              <BookDemoDialog
                defaultCourse="Immigration Consultation"
                leadType="immigration"
                trigger={
                  <Button data-testid="immig-final-cta" className="mt-8 rounded-full bg-brand-gold text-brand-navy hover:bg-brand-gold-hover font-bold px-8 h-13 btn-scale">
                    Book my free consultation
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Country details modal */}
      <CountryDetailsModal
        open={!!selectedCountry}
        onOpenChange={(v) => !v && setSelectedCountry(null)}
        country={selectedCountry}
        onBookConsultation={() => {
          // Bring the user to the primary consultation CTA at page bottom.
          document.querySelector("[data-testid='immig-final-cta']")?.scrollIntoView({ behavior: "smooth", block: "center" });
        }}
      />
    </div>
  );
}
